import { type FC, useRef, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { trackEvent } from 'dibs-tracking';
import { ItemTileCarousel } from 'dibs-search-product-tile/exports/ItemTile';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { SharedFavoriteItem } from '../../components/global/FavoriteIcons/SharedFavoriteItem';
import { BuyerProductCarousel } from 'dibs-buyer-product-tile/exports/BuyerProductTile';

import { SRC_SET_SIZES_TILE } from '../../constants/imageConstants';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useSbSelector } from '../../reducers/useSbSelector';
import { useBuyerProductTileSize } from '../hooks/useBuyerProductTileSize';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';

import styles from './SharedItemTile.scss';
import dibsCss from 'dibs-css';

import { type ItemShippingPrices } from '../../finding/hooks/useItemShippingPrices';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { type MeasurementUnit } from 'dibs-intl/exports/units';
import { type SharedItemTile_viewer$key } from './__generated__/SharedItemTile_viewer.graphql';
import { type SharedItemTile_itemSearch$key } from './__generated__/SharedItemTile_itemSearch.graphql';
import { type SharedItemTile_item$key } from './__generated__/SharedItemTile_item.graphql';
import { type SharedItemTile_user$key } from './__generated__/SharedItemTile_user.graphql';
import { getShippingCostSbVariant } from '../../utils/abTestHelper';

const itemSearchFragment = graphql`
    fragment SharedItemTile_itemSearch on ItemSearchQueryConnection {
        ...ItemTileCarousel_itemSearch
        ...BuyerProductCarousel_itemSearch
    }
`;
const itemFragment = graphql`
    fragment SharedItemTile_item on Item
    @argumentDefinitions(
        photosLimit: { type: "Int", defaultValue: 1 }
        fetchVideo: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
        showSeller: { type: "Boolean!" }
        isTrade: { type: "Boolean" }
    ) {
        ...BuyerProductCarousel_item
            @arguments(
                isTrade: $isTrade
                fetchVideo: $fetchVideo
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
                showSeller: $showSeller
            )
        ...ItemTileCarousel_item
            @arguments(
                showSeller: $showSeller
                isTrade: $isTrade
                fetchVideo: $fetchVideo
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
            )
        ...SharedFavoriteItem_item
        ...FavoritesItemProvider_item
        serviceId
        productPhotos: photos(limit: $photosLimit) {
            smallPath
            versions {
                webPath
            }
        }
    }
`;

const userFragment = graphql`
    fragment SharedItemTile_user on User {
        ...FavoritesItemProvider_user
        ...BuyerProductCarousel_user @arguments(showSeller: $showSeller)
    }
`;

const viewerFragment = graphql`
    fragment SharedItemTile_viewer on Viewer {
        ...ItemTileCarousel_viewer
            @arguments(
                fetchRegionalInfo: $fetchRegionalInfo
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
        ...BuyerProductCarousel_viewer
    }
`;

type Props = {
    viewer: SharedItemTile_viewer$key | null | undefined;
    itemSearch: SharedItemTile_itemSearch$key | null | undefined;
    item: SharedItemTile_item$key | null;
    user: SharedItemTile_user$key | null | undefined;
    favorites?: FavoritesProviderChildrenProps;
    isSponsored?: boolean;
    index: number;
    isVisuallySimilarEnabled?: boolean;
    lazyLoadStartIndex?: number;
    imageLoadVerticalOffset?: number;
    onClick?: () => void;
    onContentLoaded?: () => void;
    showInternalSellerName?: boolean;
    showSellerName?: boolean;
    showCreatorName?: boolean;
    showMeasurements?: boolean;
    showStorefrontOnlyBadge?: boolean;
    measurementUnit?: MeasurementUnit;
    beforeRender?: () => void;
    onContentVisible?: () => void;
    customClassName?: string;
    imageSize?: string;
    tileType?: string;
    showSwipeIndicator?: boolean;
    fetchFolderOnMouseOver?: boolean;
    showFavoritesTooltip?: boolean;
    additionalElements?: ReactNode;
    showPriceContainer?: boolean;
    showTitle?: boolean;
    showQuickView?: boolean;
    showMSKUSwatch?: boolean;
    showShippingBadge?: boolean;
    showNoReserveBadge?: boolean;
    urgencySignal?: string | null;
    shippingPrices?: ItemShippingPrices | null;
    hideRecentSoldPrice?: boolean;
    mlFreightQuotesAbTestVariant: string | null;
};

export const SharedItemTile: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    item: itemRef,
    user: userRef,
    isSponsored = false,
    favorites,
    index,
    isVisuallySimilarEnabled,
    lazyLoadStartIndex = 2,
    onClick,
    onContentLoaded,
    showInternalSellerName,
    showSellerName,
    showCreatorName,
    showMeasurements,
    showStorefrontOnlyBadge,
    measurementUnit,
    imageLoadVerticalOffset,
    beforeRender,
    onContentVisible,
    customClassName,
    imageSize,
    tileType,
    showSwipeIndicator,
    fetchFolderOnMouseOver,
    showFavoritesTooltip,
    additionalElements = null,
    showPriceContainer = true,
    showTitle,
    showQuickView,
    showMSKUSwatch,
    showShippingBadge = true,
    showNoReserveBadge,
    urgencySignal = null,
    shippingPrices,
    hideRecentSoldPrice = false,
    mlFreightQuotesAbTestVariant,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const tileElementRef = useRef(null);
    const itemId = item?.serviceId || '';
    const afterDisplay = useSbSelector(
        state =>
            state.relayVariables.variables.afterDisplay || state.relayVariables.variables.isClient
    );
    const {
        isMobile,
        isBot = false,
        urgencySignalsMwVariant,
        isContentVisibilitySbVariant,
        isShipsFromSBTileVariant,
        isItemTileRedesignTestVariant,
    } = useServerVarsContext();
    const { currency, showCurrency } = useCurrency();
    const reduceMargins = urgencySignalsMwVariant === 'variant' && isMobile;
    const showUrgencySignal = urgencySignalsMwVariant === 'variant' || !isMobile;
    const isShippingCostSbVariant = afterDisplay && getShippingCostSbVariant() === 'variant';
    const tileSize = useBuyerProductTileSize();

    function onQuickViewDetailsClick(): void {
        trackEvent({
            category: 'ecommerce',
            label: itemId,
            action: 'product detail view - from seo quicklook',
        });
    }

    const sharedProps = {
        item,
        index,
        imageLoadVerticalOffset,
        onClick,
        onContentLoaded,
        imageSize,
        showTitle,
        showPriceContainer,
        showQuickView,
    };
    const photo = item?.productPhotos?.[0];
    if (!photo?.smallPath && !photo?.versions?.[0]?.webPath) {
        return null;
    }

    if (isItemTileRedesignTestVariant) {
        return (
            <FavoritesItemProvider
                item={item}
                user={user}
                applyMouseEvents={fetchFolderOnMouseOver}
                className={classnames(dibsCss.wFull, dibsCss.minW0)}
            >
                {favoritesItemProps => {
                    return (
                        <BuyerProductCarousel
                            viewer={viewer}
                            itemSearch={itemSearch}
                            item={item}
                            user={user}
                            afterDisplay={afterDisplay}
                            beforeRender={beforeRender}
                            currency={currency}
                            imageBackgroundColor="moonStone"
                            imageLoadVerticalOffset={imageLoadVerticalOffset}
                            index={index}
                            isSponsored={isSponsored}
                            itemsPerRow={lazyLoadStartIndex}
                            hideRecentSoldPrice={hideRecentSoldPrice}
                            onClick={onClick}
                            onContentVisible={onContentVisible}
                            measurementUnit={measurementUnit}
                            urgencySignal={showUrgencySignal ? urgencySignal : null}
                            shippingPrices={shippingPrices}
                            showBrowseSimilar={isVisuallySimilarEnabled && afterDisplay}
                            showMeasurements={showMeasurements}
                            showPrice={(afterDisplay && showCurrency) || isBot}
                            showSwipeIndicator={showSwipeIndicator}
                            showMskuSwatch={showMSKUSwatch}
                            showShippingCost={
                                showShippingBadge &&
                                afterDisplay &&
                                showCurrency &&
                                !isShipsFromSBTileVariant
                            }
                            hasQuickView={showQuickView}
                            showQuickViewButton={!!afterDisplay}
                            onQuickViewDetailsClick={onQuickViewDetailsClick}
                            size={tileSize}
                            srcSet={{ sizes: SRC_SET_SIZES_TILE }}
                            favoritesComponent={
                                <SharedFavoriteItem
                                    {...favoritesItemProps}
                                    item={item}
                                    isSponsored={isSponsored}
                                    favorites={favorites}
                                    fetchFolderOnMouseOver={fetchFolderOnMouseOver}
                                    showFavoritesTooltip={showFavoritesTooltip}
                                    tileSize={tileSize}
                                />
                            }
                            isShippingCostSbVariant={isShippingCostSbVariant}
                        />
                    );
                }}
            </FavoritesItemProvider>
        );
    }

    return (
        <div
            className={classnames(styles.tileWrapper, {
                [styles.tileWidth]: !customClassName,
                ...(customClassName ? { [customClassName]: true } : {}),
                [styles.completeInfo]: true,
            })}
            data-tn="item-tile-wrapper"
            data-tile-index={index}
            ref={tileElementRef}
        >
            {additionalElements}
            <FavoritesItemProvider
                item={item}
                user={user}
                applyMouseEvents={fetchFolderOnMouseOver}
                className={dibsCss.wFull}
            >
                {favoritesItemProps => {
                    return (
                        <ItemTileCarousel
                            {...sharedProps}
                            isSponsored={isSponsored}
                            useLoFiLazyLoader
                            itemsPerRow={lazyLoadStartIndex}
                            isMobile={isMobile}
                            beforeRender={beforeRender}
                            onContentVisible={onContentVisible}
                            srcSetSizes={SRC_SET_SIZES_TILE}
                            showQuickViewIcon={!!afterDisplay}
                            showSellerName={showSellerName}
                            showCreatorName={showCreatorName}
                            showInternalSellerName={showInternalSellerName}
                            showStorefrontOnlyBadge={showStorefrontOnlyBadge}
                            currency={currency}
                            showPrice={(afterDisplay && showCurrency) || isBot}
                            measurementUnit={measurementUnit}
                            onQuickViewDetailsClick={onQuickViewDetailsClick}
                            favoritesComponent={
                                <SharedFavoriteItem
                                    {...favoritesItemProps}
                                    item={item}
                                    isSponsored={isSponsored}
                                    favorites={favorites}
                                    fetchFolderOnMouseOver={fetchFolderOnMouseOver}
                                    showFavoritesTooltip={showFavoritesTooltip}
                                    tileSize={tileSize}
                                />
                            }
                            showMeasurements={showMeasurements}
                            showBrowseSimilar={isVisuallySimilarEnabled && afterDisplay}
                            viewer={viewer}
                            tileType={tileType}
                            itemSearch={itemSearch}
                            showSwipeIndicator={showSwipeIndicator}
                            showMSKUSwatch={showMSKUSwatch}
                            showShippingBadge={
                                showShippingBadge &&
                                afterDisplay &&
                                showCurrency &&
                                !isShipsFromSBTileVariant
                            }
                            showNoReserveBadge={showNoReserveBadge}
                            hideRecentSoldPrice={hideRecentSoldPrice}
                            urgencySignal={showUrgencySignal ? urgencySignal : null}
                            shippingPrices={shippingPrices}
                            mlFreightQuotesAbTestVariant={mlFreightQuotesAbTestVariant}
                            isShippingCostSbVariant={isShippingCostSbVariant}
                            reduceMargins={reduceMargins}
                            shouldAutoHideContent={isContentVisibilitySbVariant}
                            showShipsFrom={isShipsFromSBTileVariant}
                        />
                    );
                }}
            </FavoritesItemProvider>
        </div>
    );
};
